import type { StockInfo } from '../../../product/index';
import { isMaxOrderableExceededInCart } from '../../../shared/utils/stockValidation';
import { Effect } from 'effect';

import { Cart } from '@vs-shop/engine/capability';
import { writeIntoCart } from '@vs-shop/engine/capability/cart/cartToken';
import { CartError } from '@vs-shop/engine/capability/cart/errors';
import type { CartWriteError } from '@vs-shop/engine/capability/cart/errors';
import { isBackendApiError } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { MaxOrderableInCartError } from '../../data/error/orderClient';
import { TenantConfig } from '../../service/tenant/TenantConfig';
import type { ProductForGTM } from '../../utils/gtm/create/cart-items-update';
import { createAddToCartPayloadFromProduct } from '../../utils/gtm/create/cart-items-update';
import { sendGtmEvent } from '../../utils/gtm/gtm';

type AddProductVariantToOrderP = {
  code: string;
  stock: StockInfo;
};

export type AddProductVariantToOrderDataProduct = AddProductVariantToOrderP & ProductForGTM;

export type AddProductVariantToOrderData = {
  product: AddProductVariantToOrderDataProduct;
  quantity: number;
  quantityInCart: number;
};

/**
 * A 500 that PHP answered to an add. RCProfi's backend answers it to every
 * write into a cart that it can no longer process, for example a cart with two
 * shipments. The cart still reads, so only a new cart lets the shopper go on.
 */
const cartRefusesEveryAdd = (error: CartWriteError) =>
  error instanceof CartError.Unavailable &&
  isBackendApiError(error.cause) &&
  error.cause.status === 500;

/**
 * Adds one variant to the cart: the stock guard, then the backend write.
 *
 * `quantityInCart` is the quantity the cart already holds. When the backend
 * reports the cart as gone, the engine checks the cart before replacing its
 * token. A `500` from PHP also replaces the token, and the add runs once
 * against a new cart. The lines of the old cart stay behind.
 */
export function writeProductVariantToOrder(data: AddProductVariantToOrderData) {
  return Effect.gen(function* () {
    const maxOrderable = data.product.stock.eshop.maxOrderable;

    if (isMaxOrderableExceededInCart(maxOrderable, data.quantityInCart)) {
      return yield* new MaxOrderableInCartError({
        productVariant: data.product.code,
        quantityInCart: data.quantityInCart,
        maxOrderable,
        message: 'Cart already contains maximum stock quantity',
      });
    }

    return yield* writeIntoCart(
      (token) =>
        Cart.pipe(
          Effect.flatMap((cart) =>
            cart.addItem({ token, variantCode: data.product.code, quantity: data.quantity })
          )
        ),
      { spentOn: cartRefusesEveryAdd }
    );
  });
}

/** Sends the GTM `add_to_cart` event for a line the cart holds. */
export function trackProductVariantAddedToOrder(
  product: AddProductVariantToOrderDataProduct,
  quantity: number
) {
  return Effect.gen(function* () {
    const { currency } = yield* TenantConfig;

    yield* createAddToCartPayloadFromProduct(product, quantity, currency).pipe(
      Effect.flatMap((event) => sendGtmEvent(event, true))
    );
  });
}
